// A stable name for the surface a URL represents, for page-scoped analytics (see setFullStoryPage).
//
// Two constraints shape this:
//   1. `pageName` must be constant for a given path — FullStory rejects a name change within one page
//      (page = one host/path). Deriving the name from the FIRST path segment alone guarantees that.
//   2. Anything that varies without a path change goes in `pageStep`, which is allowed to move and
//      merges across calls. That covers the settings modal, whose tabs used to live behind
//      `?manage=subscription` on a single path.
//
// `pageStep` is deliberately low-cardinality: ids never appear in it, so "how many people reached the
// plan picker" is one number rather than one per project.

export interface PageIdentity {
  pageName: string
  properties: Record<string, string>
}

const PAGE_NAMES: Record<string, string> = {
  '': 'Dashboard',
  new: 'New project',
  projects: 'Projects',
  p: 'Embedded project',
  settings: 'Settings',
  insights: 'Insights',
  'release-notes': 'Release notes',
  admin: 'Admin',
  invite: 'Project invite',
  join: 'Team invite',
  'verify-email': 'Verify email',
  github: 'GitHub setup',
}

function titleCase(segment: string): string {
  const words = segment.replace(/[-_]+/g, ' ').trim()
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'App'
}

// The step within a route family, for the families that have one. Anything else gets no step rather
// than a guessed one, since the remaining routes carry an id in that position.
function pageStepFor(root: string, parts: string[], search: string): string | null {
  if (root === 'settings') {
    const slug = parts[1] ?? 'account'
    // The plan picker spent a while as a query param on the account path. Report it under the same
    // step as the route that replaced it, so the funnel spans the change instead of restarting at it.
    if (new URLSearchParams(search).get('manage') === 'subscription') return 'subscription'
    return slug
  }
  if (root === 'projects' || root === 'p') {
    if (!parts[1]) return 'list'
    return parts[2] ?? 'overview'
  }
  if (root === 'admin') return parts[1] ?? 'overview'
  return null
}

/** Resolve a location into the page name and properties to report. */
export function describePage(pathname: string, search: string = ''): PageIdentity {
  const parts = pathname.split('/').filter(Boolean)
  const root = parts[0] ?? ''
  const pageName = PAGE_NAMES[root] ?? titleCase(root)
  const pageStep = pageStepFor(root, parts, search)
  return { pageName, properties: pageStep ? { pageStep } : {} }
}
